<template>
  <AccordionCard
    v-show="!cardSelected || (isEditing && isCollapsed)"
    :hideChevrons="true"
    :isCollapsed="isCollapsed">
    <template slot="title">{{ cardTitle }}</template>
    <template slot="buttons">
      <button
        type="button"
        class="btn btn-md btn-outline-hover-secondary kt-label-font-color-2--off text-danger"
        @click="downloadPDF({ preview: false, compressed: true })"
        :disabled="isPDFDownloadingHQ || isPDFDownloadingLQ || isPDFPreviewing "
        v-if="enableHQAndHL && !isCollapsed">
        <span v-if="!isPDFDownloadingLQ">
          <font-awesome-icon :icon="['fas', 'download']" class="mr-1" /> Download (LQ)
        </span>
        <span v-else>
          <font-awesome-icon :icon="['fas', 'spinner']" class="mr-1" spin /> Generating PDF (LQ)
        </span>
      </button>

      <button
        type="button"
        class="btn btn-md btn-outline-hover-secondary kt-label-font-color-2--off text-danger"
        @click="downloadPDF({ preview: true, compressed: false })"
        :disabled="isEditing || isPDFDownloadingHQ || isPDFDownloadingLQ || isPDFPreviewing"
        v-if="!isCollapsed">
        <span v-if="!isPDFPreviewing">
          <font-awesome-icon :icon="['fas', 'eye']" class="mr-1" /> Preview
        </span>
        <span v-else>
          <font-awesome-icon :icon="['fas', 'spinner']" class="mr-1" spin /> Loading preview
        </span>
      </button>
      <button
        class="btn btn-outline-brand btn-outline-danger btn-elevate btn-circle btn-icon btn-sm"
        :disabled="isEditing || isPDFDownloadingHQ || isPDFDownloadingLQ || isPDFPreviewing"
        @click="() => {emitSelectAccordionCard(cardName);resetSelectedSections();}"
        v-if="!isCollapsed">
        <font-awesome-icon :icon="['fas', 'pen']" />
      </button>
      <button
        class="btn btn-md btn-outline-hover-secondary kt-label-font-color-2"
        :disabled="isSaving"
        @click="cancelCustomPDF"
        v-if="isCollapsed">
        <span v-if="selectedTab !== tabs.PREVIEW_TAB">
          <font-awesome-icon :icon="['fas', 'times']" class="mr-1" /> Cancel
        </span>
        <span v-else>
          <font-awesome-icon :icon="['fas', 'chevron-left']" class="mr-1" /> Back
        </span>
      </button>
      <button
        class="btn btn-sm btn-wide btn-primary"
        :disabled="isSaving"
        @click="save"
        v-if="isCollapsed && selectedTab !== tabs.PREVIEW_TAB">
        <font-awesome-icon :icon="['fas', 'check']" class="mr-1" v-if="!isSaving" />
        <font-awesome-icon :icon="['fas', 'spinner']" spin class="mr-1" v-if="isSaving" /> Done
      </button>
    </template>

    <ul class="nav nav-tabs nav-tabs-line nav-tabs-bold nav-tabs-line-3x nav-tabs-line-info mb-4" role="tablist"
      v-if="selectedTab !== tabs.PREVIEW_TAB">
      <li class="nav-item">
        <a
          href=""
          role="tab"
          class="nav-link"
          :class="{ 'active': selectedTab === tabs.SELECT_IMAGES_TAB }"
          @click.prevent="selectTab(tabs.SELECT_IMAGES_TAB)">&nbsp;&nbsp;Select images&nbsp;&nbsp;</a>
      </li>
      <li class="nav-item">
        <a
          href=""
          role="tab"
          class="nav-link"
          :class="{ 'active': selectedTab === tabs.LINE_BREAKS_NUMBER_TAB }"
          @click.prevent="selectTab(tabs.LINE_BREAKS_NUMBER_TAB)">&nbsp;&nbsp;Line breaks number&nbsp;&nbsp;</a>
      </li>
      <li class="nav-item">
        <a
          href=""
          role="tab"
          class="nav-link"
          :class="{ 'active': selectedTab === tabs.SELECT_SECTIONS_TAB }"
          @click.prevent="selectTab(tabs.SELECT_SECTIONS_TAB)">&nbsp;&nbsp;Sections&nbsp;&nbsp;</a>
      </li>
    </ul>

    <div class="container-fluid" v-show="selectedTab === tabs.PREVIEW_TAB">
      <div class="mb-4">
          <h1 class="text-info mb-2">Preview</h1>
          <PDFViewer v-if="pdfData" :data="pdfData" />
        </div>
    </div>

    <div class="container-fluid" v-show="selectedTab === tabs.SELECT_IMAGES_TAB">
      <div class="row my-5">
        <div class="form-group col-md-2">
          <label class="labelTitle">Select your cover image(s)</label>
        </div>
        <div class="col-md-10">
          <ImagePicker
            :isDuplicated="false"
            :imageUrl="getCoverImageUrl"
            :isEditing="true"
            :total="totalImages"
            :images="images"
            :countries="countries"
            :isSearching="isImagePickerSearching"
            :defaultParams="defaultImagePickerParams"
            :limitMaxOfSelectedImages="limitMaxOfSelectedImagesCover"
            :labelSearch="`Select image`"
            :labelButton="`Select image`"
            :disableButton="isSaving"
            @onSearch="onImagePickerSearch"
            @onSelectImage="onImagePickerSelectCoverImage"
            style="width: 150px;height: auto;" />
            <div class="text-danger my-3" v-if="hasErrorsCover">This field is required.</div>
        </div>
      </div>
      <div class="row">
        <div class="form-group col-md-2">
          <label class="labelTitle">Select your image(s) template</label>
        </div>
        <div class="col-md-10">
          <ImagePicker
            v-if="countries"
            :isDuplicated="false"
            :imageUrl="getTemplateImageUrl"
            :isEditing="true"
            :total="totalImages"
            :images="images"
            :countries="countries"
            :isSearching="isImagePickerSearching"
            :defaultParams="defaultImagePickerParams"
            :limitMinOfSelectedImages="limitMinOfSelectedImagesTemplate"
            :limitMaxOfSelectedImages="limitMaxOfSelectedImagesTemplate"
            :labelSearch="`Select ${limitMaxOfSelectedImagesTemplate} image(s)`"
            :labelButton="`Select ${limitMaxOfSelectedImagesTemplate} image(s)`"
            :disableButton="isSaving"
            @onSearch="onImagePickerSearch"
            @onSelectImage="onImagePickerSelectTemplateImages"
            style="width: 150px;height: auto;" />
            <div class="text-danger my-3" v-if="hasErrorsTemplate">This field is required and It needs to select {{ limitMaxOfSelectedImagesTemplate }} image(s).</div>
        </div>
      </div>
    </div>

    <div class="container-fluid" v-show="selectedTab === tabs.LINE_BREAKS_NUMBER_TAB">
      <TabShareLineBreaksNumber
        :data="dayList"
        :isSaving="isSaving" />
    </div>

    <div class="container-fluid" v-show="selectedTab === tabs.SELECT_SECTIONS_TAB">
      <p class="my-2 mb-4">Please select the section to be included into the quote for the PDF and Weblink</p>
      <div class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input
            type="checkbox"
            v-model="selectedSections.dates.active"
            :disabled="isTrafalgar"
          > Display Dates over Cover & Itineraries <span></span>
        </label>
      </div>
      <div class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input
            type="checkbox"
            v-model="selectedSections.cover.active"
            checked
            disabled
          > Cover <span></span>
        </label>
      </div>
      <div class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input
          type="checkbox"
          v-model="selectedSections.letter.active"
          :disabled="isTrafalgar"
          > Letter <span></span>
        </label>
      </div>
      <div class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input
            type="checkbox"
            v-model="selectedSections.inclusions.active"
            :disabled="isTrafalgar"
          > Inclusions <span></span>
        </label>
      </div>
      <div class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input type="checkbox" v-model="selectedSections.itinerary.active"
          checked
          disabled
          > Itinerary <span></span>
        </label>
      </div>
      <div class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input
            type="checkbox"
            v-model="selectedSections.pricing.active"
            :disabled="isTrafalgar"
          > Pricing <span></span>
        </label>
      </div>
      <div v-if="isTrafalgar" class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input
            type="checkbox"
            v-model="selectedSections.airContract.active"
            :disabled="isDA"
          > Air Contract <span></span>
        </label>
      </div>
      <div class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input
          type="checkbox"
          v-model="selectedSections.termsConditions.active"
          :disabled="isTrafalgar"
          > Terms & Conditions <span></span>
        </label>
      </div>
      <div class="colCheckboxRequest form-group col col-6 col-md-4">
        <label class="kt-checkbox kt-checkbox--bold">
          <input
            type="checkbox"
            v-model="selectedSections.bookMyGroup.active"
            :disabled="isTrafalgar"
          > Book My Group <span></span>
        </label>
      </div>
    </div>
  </AccordionCard>
</template>

<script>
import { mapGetters } from 'vuex'
import NProgress from 'nprogress'
import cloneDeep from 'lodash.clonedeep'

import { buildNewAssets, getValidatedImages, getValidatedExportSection } from '@/helpers'

import { OPPORTUNITY_EXPORT_TYPES, OPPORTUNITY_EXPORT_SECTIONS_IDS } from '@/constants'

import AccordionCard from '@/components/_shared/accordion/AccordionCard.vue'
import ImagePicker from '@/components/_shared/image-picker/ImagePicker.vue'
import PDFViewer from '@/components/_shared/PDFViewer.vue'
import TabShareLineBreaksNumber from '@/components/opportunity-edit/tab-share-line-breaks-number/TabShareLineBreaksNumber.vue'

export default {
  name: 'TabShareQuotePdf',
  data () {
    const PREVIEW_TAB = 'preview-tab'
    const SELECT_IMAGES_TAB = 'select-images-tab'
    const LINE_BREAKS_NUMBER_TAB = 'line-breaks-number-tab'
    const SELECT_SECTIONS_TAB= 'select-sections-tab'

    const IDS_BRANDS_SELECT_SECTIONS = [13, 14]

    return {
      IDS_BRANDS_SELECT_SECTIONS,
      isPDFDownloadingHQ: false,
      isPDFDownloadingLQ: false,
      isPDFPreviewing: false,
      coverImageSelected: null,
      templateImagesSelected: null,
      images: [],
      totalImages: 0,
      isImagePickerSearching: false,
      limitMaxOfSelectedImagesCover: 1,
      limitMaxOfSelectedImagesTemplate: 4,
      limitMinOfSelectedImagesTemplate: 2,
      isSaving: false,
      hasErrorsCover: false,
      hasErrorsTemplate: false,
      isPreviewing: false,
      selectedTab: SELECT_IMAGES_TAB,
      tabs: {
        PREVIEW_TAB,
        SELECT_IMAGES_TAB,
        LINE_BREAKS_NUMBER_TAB,
        SELECT_SECTIONS_TAB
      },
      dayList: [],
      pdfData: '',
      templateContentType: this.pdfContentType,
      enableHQAndHL: true,
      selectedSections: this.getValidatedSelectedSections()
    }
  },
  components: {
    AccordionCard,
    ImagePicker,
    PDFViewer,
    TabShareLineBreaksNumber
  },
  computed: {
    ...mapGetters({
      'locationCountries': 'location/countries',
      'opportunityData': 'opportunity/opportunity',
      'opportunityDetails': 'opportunity/opportunityDetails',
      'itinerarySourceTypeSelected': 'opportunity/itinerarySourceTypeSelected'
    }),
    countries () {
      if (Array.isArray(this.locationCountries) && this.locationCountries.length > 0) {
        return this.locationCountries
      }
      return []
    },
    defaultImagePickerParams () {
      return {
        limit: 21,
        offset: 0,
        w: 150,
        h: 150,
        country: '',
        city: ''
      }
    },
    getCoverImageUrl () {
      if (this.coverImageSelected && this.coverImageSelected.url_image) {
        return this.coverImageSelected.url_image
      }
      if (this.data &&
          Array.isArray(this.data[this.coverPageName]) &&
          this.data[this.coverPageName].length > 0) {
        return this.data[this.coverPageName][0].url_image
      }
      return ''
    },
    getTemplateImageUrl () {
      if (
        this.templateImagesSelected &&
        Array.isArray(this.templateImagesSelected) &&
        this.templateImagesSelected.length > 0) {
        return this.templateImagesSelected[0].url_image
      }
      if (this.data &&
          Array.isArray(this.data[this.imagePageName]) &&
          this.data[this.imagePageName].length > 0) {
        return this.data[this.imagePageName][0].url_image
      }
      return ''
    },
    isDA() {
      return this.opportunityData.opportunity_details.id_brand === 13;
    },
    isTrafalgar() {
      return this.opportunityData.opportunity_details.id_brand === 14;
    },
  },
  props: {
    cardSelected: {
      type: String,
      required: true
    },
    cardName: {
      type: String,
      required: true
    },
    cardTitle: {
      type: String,
      required: true
    },
    pdfContentType: {
      type: String,
      required: true
    },
    webContentType: {
      type: String,
      required: true
    },
    coverPageName: {
      type: String,
      required: true
    },
    imagePageName: {
      type: String,
      required: true
    },
    isAgent: {
      type: Boolean,
      required: true
    },
    isCollapsed: {
      type: Boolean,
      required: true
    },
    isEditing: {
      type: Boolean,
      required: true
    },
    isCompletedQuote: {
      type: Boolean,
      default: false
    },
    data: {
      type: [Array, Object],
      required: false
    },
    templateSetting: {
      type: [Array, Object],
      required: false,
    }
  },
  methods: {
    resetSelectedSections () {
      this.selectedSections = this.getValidatedSelectedSections()
    },
    getValidatedSelectedSections() {
      return {
        dates: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.DATES),
        cover: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.COVER),
        letter: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.LETTER),
        itinerary: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.ITINERARY),
        inclusions: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.INCLUSIONS),
        map: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.MAP),
        pricing: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.PRICING),
        airContract: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.AIR_CONTRACT),
        termsConditions: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.TERMS_CONDITIONS),
        bookMyGroup: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.BOOK_MY_GROUP),
        contactUs: this.validateExportSection(OPPORTUNITY_EXPORT_SECTIONS_IDS.CONTACT_US)
      }
    },
    validateExportSection(exportSectionId = 0) {
      return getValidatedExportSection(this.templateSetting, exportSectionId, this.templateContentType)
    },
    selectTab (tabName) {
      this.populateDayList(this.templateContentType)
      this.selectedTab = tabName
    },
    isValidBrandForChoosingSections () {
      const idBrand = this.opportunityData.opportunity_details.id_brand
      const brandFound = this.IDS_BRANDS_SELECT_SECTIONS.includes(idBrand)
      return brandFound;
    },
    openPreview () {
      this.emitSelectAccordionCard(this.cardName)
      this.selectedTab = this.tabs.PREVIEW_TAB
    },
    populateDayList (contentType) {
      const validation = this.opportunityData.itineraries[this.itinerarySourceTypeSelected.index].days.filter(d => d.id === null)

      if (Array.isArray(validation) && validation.length > 0) {
        this.$notification.warning('Save the opportunity before', 5000)
      }

      if (this.opportunityData) {
        this.dayList = []
        this.dayList = this.opportunityData.itineraries[this.itinerarySourceTypeSelected.index].days.filter(d => d.id).map(d => {
          const templateContentDaysData = this.opportunityData.template_content
          let templateContentDays = [{ id: null, type: contentType, days: [] }]
          if (templateContentDaysData) {
            templateContentDays = templateContentDaysData.find(t => t.type === contentType)
          }
          let dayFound = { line_breaks_number: 0 }
          if (templateContentDays && Array.isArray(templateContentDays.days) && templateContentDays.days.length > 0) {
            dayFound = templateContentDays.days.find(cd => cd.startday === d.startday)
            if (!dayFound) {
              dayFound = { line_breaks_number: 0 }
            }
          }
          return {
            id: d.id,
            title: d.title,
            startday: d.startday,
            line_breaks_number: Number(dayFound.line_breaks_number)
          }
        })
      }
    },
    isItineraryValid () {
      const validation = this.opportunityData.itineraries[this.itinerarySourceTypeSelected.index].days.filter(d => d.id === null)
      return !(Array.isArray(validation) && validation.length > 0)
    },
    emitSelectAccordionCard (cardName) {
      if (!this.isItineraryValid()) {
        this.$notification.warning('You have pending changes in your itinerary, please save before entry this section.', 9000)
        return false
      }
      this.$emit('onSelect', cardName)
    },
    downloadPDF (options = { preview: false, compressed: false }) {
      const uuid = this.$route.params.privateId

      let payload = {
        uuid,
        compressed: options.compressed,
        sourceType: 'ITP',
        agent: this.isAgent,
      }

      this.$emit('onEditing')

      if (options.preview) {
        this.isPDFPreviewing = true
        payload['preview_section'] = 'itinerary'
      } else {
        this.isPDFDownloadingHQ = !options.compressed
        this.isPDFDownloadingLQ = options.compressed
      }

      this.$store
        .dispatch('opportunity/downloadQuotePDF', payload)
        .then(response => {
          if (options.preview) {
            this.pdfData = ''
            this.pdfData = response
            this.openPreview()
          }
          this.isPDFDownloadingHQ = false
          this.isPDFDownloadingLQ = false
          this.isPDFPreviewing = false
          this.$notification.success('Download complete!')
          this.$emit('onEditing')
        })
        .catch(error => {
          console.log(error)
          this.isPDFDownloadingHQ = false
          this.isPDFDownloadingLQ = false
          this.isPDFPreviewing = false
          this.$notification.error('Download failed')
          this.$emit('onEditing')
        })
    },
    onImagePickerSearch (params) {
      if (params.country === '' && params.city === '') {
        this.isImagePickerSearching = false
        this.totalImages = 0
        this.images = []
        return false
      }
      this.isImagePickerSearching = true
      this.$store.dispatch('asset/getAssets', params)
        .then(response => {
          this.images = response.asset // Array images
          this.totalImages = response.total
          this.isImagePickerSearching = false
        })
        .catch(error => {
          console.log(error)
          this.isImagePickerSearching = false
          this.totalImages = 0
          this.images = []
        })
    },
    onImagePickerSelectCoverImage (imageSelected) {
      this.coverImageSelected = imageSelected
    },
    onImagePickerSelectTemplateImages (imagesSelected) {
      this.templateImagesSelected = imagesSelected
    },
    cancelCustomPDF () {
      this.coverImageSelected = null
      this.templateImagesSelected = null
      this.resetSelectedSections()
      this.emitSelectAccordionCard('')
      this.selectedTab = this.tabs.SELECT_IMAGES_TAB
      this.pdfData = ''
    },
    save () {
      if (this.selectedTab === this.tabs.SELECT_IMAGES_TAB) {
        this.saveSelectImagesTab()
      } else if (this.selectedTab === this.tabs.LINE_BREAKS_NUMBER_TAB) {
        this.saveLineBreaksNumberTab(this.templateContentType)
      } else if (this.selectedTab === this.tabs.SELECT_SECTIONS_TAB) {
        this.saveSelectSectionsTab()
      }
    },
    getUpdatedSelectedSectionsByExportType (currentContents = []) {
      return Object.values(this.selectedSections).map(section => {
        const currentContent = currentContents.find(content => content.id_content === section.id_content)
        return {
          ...section,
          id: currentContent !== undefined ? currentContent.id : null,
          status: section.active ? 1 : -1
        }
      }).filter(section => !(section.status === -1 && [undefined, null].includes(section.id)));
    },
    saveSelectSectionsTab () {
      if (!this.isValidBrandForChoosingSections()) return this.$notification.warning('Feature not available for this brand.', 5000);

      const pastOpportunitySettings = this.templateSetting || [];

      const quotePdfExists = pastOpportunitySettings.some(opportunitySetting =>
        opportunitySetting.type === this.templateContentType
      );
      const weblinkExists = pastOpportunitySettings.some(opportunitySetting =>
        opportunitySetting.type === this.webContentType
      );

      const adaptedTemplateSections = pastOpportunitySettings.map(opportunitySetting => {
        if (opportunitySetting.type === this.templateContentType && quotePdfExists) {
          return {
            ...opportunitySetting,
            contents: this.getUpdatedSelectedSectionsByExportType(opportunitySetting.contents),
            id: opportunitySetting.id
          };
        }
        if (opportunitySetting.type === this.webContentType && weblinkExists) {
          return {
            ...opportunitySetting,
            contents: this.getUpdatedSelectedSectionsByExportType(opportunitySetting.contents),
            id: opportunitySetting.id
          };
        }
        return opportunitySetting;
      });

      if (!quotePdfExists) {
        adaptedTemplateSections.push({
          id: null,
          type: this.templateContentType,
          contents: this.getUpdatedSelectedSectionsByExportType([])
        });
      }
      if (!weblinkExists) {
        adaptedTemplateSections.push({
          id: null,
          type: this.webContentType,
          contents: this.getUpdatedSelectedSectionsByExportType([])
        });
      }

      let newOpportunityData = cloneDeep(this.opportunityData)
      newOpportunityData.opportunity_settings = adaptedTemplateSections

      this.$store.commit('opportunity/TEMP_SAVE_OPPORTUNITY_SETTINGS', adaptedTemplateSections )

      this.isSaving = true

      this.$store
        .dispatch('opportunity/saveOpportunity')
        .then((response) => {
          if (response.data && response.data.uuidOpportunity) {
            const privateId = response.data.uuidOpportunity
            this.$notification.success(response.message)
            this.$notification.warning('Now try to preview your changes.', 5000)
            this.emitSelectAccordionCard('')
            this.$store
              .dispatch('opportunity/getOpportunity', privateId)
              .then((response) => {
                this.isSaving = false
                NProgress.done()
                this.selectedTab = this.tabs.SELECT_SECTIONS_TAB
              })
              .catch(error => {
                console.log(error)
              })
          } else {
            this.isSaving = false
            NProgress.done()
            this.$notification.error(response.message)
          }
        })
        .catch(error => {
          console.log(error)
          this.isSaving = false
          NProgress.done()
        })
    },
    saveSelectImagesTab () {
      if (!this.coverImageSelected) {
        if (this.data &&
          Array.isArray(this.data[this.coverPageName]) &&
          this.data[this.coverPageName].length === this.limitMaxOfSelectedImagesCover) {
          this.coverImageSelected = this.data[this.coverPageName]
        }
      }

      if (!this.templateImagesSelected) {
        if (this.data &&
          Array.isArray(this.data[this.imagePageName]) &&
          (this.data[this.imagePageName].length >= this.limitMinOfSelectedImagesTemplate || this.data[this.imagePageName].length <= this.limitMaxOfSelectedImagesTemplate)) {
          this.templateImagesSelected = this.data[this.imagePageName]
        }
      }

      let coverImage = null

      // if (!Array.isArray(this.coverImageSelected)) { // IsAnObject
      if (this.coverImageSelected === null) {
        coverImage = [{ id: null }]
      } else if (!Array.isArray(this.coverImageSelected) && typeof this.coverImageSelected === 'object') { // IsAnObject
        coverImage = [{ id: null, asset_id: this.coverImageSelected.assetId }]
      } else if (Array.isArray(this.coverImageSelected)) { // IsAnArray
        coverImage = buildNewAssets(this.coverImageSelected);
      }

      const imagesTemplate = buildNewAssets(this.templateImagesSelected)

      const templateImageSize = this.templateImagesSelected.length;

      const coverPageImagesValidated = getValidatedImages(this.opportunityData.images[this.coverPageName], coverImage, 1)

      const imagesPageImagesValidated = getValidatedImages(this.opportunityData.images[this.imagePageName], imagesTemplate, templateImageSize)

      const payload = [
        {
          type: this.coverPageName,
          images: coverPageImagesValidated,
        },
        {
          type: this.imagePageName,
          images: imagesPageImagesValidated,
        },
      ]

      this.$store.commit('opportunity/TEMP_SAVE_SELECT_IMAGES', payload)

      if (coverPageImagesValidated && coverPageImagesValidated.length > 0) {
        this.$store.commit('opportunity/TEMP_EXPORT_COVER_PAGE_FROM_OPPORTUNITY_TO_CONSUMER_SECTION', [payload[0]])
      }
      if (imagesPageImagesValidated && imagesPageImagesValidated.length > 0) {
        this.$store.commit('opportunity/TEMP_EXPORT_IMAGES_PAGE_FROM_OPPORTUNITY_TO_CONSUMER_SECTION', [payload[1]])
      }

      this.isSaving = true

      this.$store
        .dispatch('opportunity/saveOpportunity')
        .then((response) => {
          if (response.data && response.data.uuidOpportunity) {
            const privateId = response.data.uuidOpportunity
            this.$notification.success(response.message)
            this.$notification.warning('Now try to download the custom PDF with its new settings.', 5000)
            this.emitSelectAccordionCard('')
            this.coverImageSelected = null
            this.templateImagesSelected = null
            this.$store
              .dispatch('opportunity/getOpportunity', privateId)
              .then((response) => {
                this.isSaving = false
                NProgress.done()
              })
              .catch(error => {
                if (error.response && error.response.status > 400) { // 401 || 403
                  // this.$store.dispatch('auth/removeToken')
                }
              })
          } else {
            this.isSaving = false
            NProgress.done()
            this.$notification.error(response.message)
          }
        })
        .catch(error => {
          console.log(error)
          this.isSaving = false
          NProgress.done()
        })
    },
    saveLineBreaksNumberTab (contentType) {
      const validation = this.dayList.filter(d => d.id === null)
      const days = this.dayList

      if (Array.isArray(validation) && validation.length > 0) {
        this.$notification.warning('Something went wrong while fetching days', 5000)
        return false
      }

      this.$store.commit('opportunity/TEMP_SAVE_LINE_BREAKS_NUMBER', { type: contentType, days })

      this.$store.commit('opportunity/TEMP_EXPORT_LINE_BREAKS_NUMBER_FROM_QUOTE_PDF_TO_FLYER_PDF', { type: contentType, days })

      this.isSaving = true

      this.$store
        .dispatch('opportunity/saveOpportunity')
        .then((response) => {
          if (response.data && response.data.uuidOpportunity) {
            const privateId = response.data.uuidOpportunity
            this.$notification.success(response.message)
            this.$notification.warning('Now try to preview your changes.', 5000)
            this.emitSelectAccordionCard('')
            this.$store
              .dispatch('opportunity/getOpportunity', privateId)
              .then((response) => {
                this.isSaving = false
                NProgress.done()
                this.selectedTab = this.tabs.SELECT_IMAGES_TAB
              })
              .catch(error => {
                if (error.response && error.response.status > 400) { // 401 || 403
                  // this.$store.dispatch('auth/removeToken')
                }
              })
          } else {
            this.isSaving = false
            NProgress.done()
            this.$notification.error(response.message)
          }
        })
        .catch(error => {
          console.log(error)
          this.isSaving = false
          NProgress.done()
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.nav-tabs.nav-tabs-line {
  border-bottom: 1px solid #ebedf2 !important;
}
</style>
